import React, { useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FunctionField,
  List,
  Loading,
  ResourceContextProvider,
  SearchInput,
  useQuery,
  TextField,
  ShowProps,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import CustomArrayChipField from "components/CustomArrayChipField";
import { GridinRowsWithSpace } from "utils/style";
import CustomDateField from "components/CustomDateField";
import CustomTimeField from "components/CustomTimeField";
import moment from "moment";
import { ETypeHoliday } from "pages/holidays/const";

const useStyles = makeStyles({
  message: {
    textAlign: "center",
    margin: "1em",
    color: "inherit",
  },
  icon: {
    width: "9em",
    height: "9em",
    opacity: 0.5,
  },
  toolbar: {
    textAlign: "center",
    marginTop: "2em",
  },
  noRecordLabel: {
    opacity: 0.5,
  },
  academicYearButton: {
    marginTop: "20px",
  },
  academicYearLabel: {
    marginTop: "5px",
  },
});

const ThisFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const Schedule = (props: ShowProps) => {
  const classes = useStyles();
  const [academicYearId, setAcademicYearId] = useState<number>();

  const { data, loading, error } = useQuery({
    type: "getList",
    resource: "academicYears",
    payload: {
      sort: { field: "id", order: "ASC" },
      filter: {
        SchoolId: parseInt(props.id),
        Date: moment().format("Y-MM-DD"),
      },
    },
  });

  // useEffect for set default AcademicYearId
  // i assume the default is a min index of AcademicYearId
  useEffect(() => {
    if (data) {
      setAcademicYearId(data[0]?.id);
    }
  }, [data]);

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR: {error}</p>;
  }

  return (
    <>
      <SanitizedGrid container spacing={2}>
        {data.length > 0 ? (
          <>
            <Grid item xs={12}>
              <Typography variant="h6">
                Current Academic Year : {data[0].name}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Academic Terms</Typography>
            </Grid>

            <Grid item xs={12}>
              <ResourceContextProvider value="academicTerms">
                <List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<ThisFilter />}
                  filterDefaultValues={{ AcademicYearId: academicYearId }}
                  bulkActionButtons={false}
                  title=" "
                >
                  <Datagrid>
                    <TextField source="name" label="Term" />
                    <CustomDateField source="startDate" label="From" />
                    <CustomDateField source="endDate" label="To" />
                  </Datagrid>
                </List>
              </ResourceContextProvider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Bus Schedules</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Going to School</Typography>
            </Grid>

            <Grid item xs={12}>
              <ResourceContextProvider value="busSchedules">
                <List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<ThisFilter />}
                  filterDefaultValues={{
                    SchoolId: parseInt(props.id),
                    Type: "ToSchool",
                  }}
                  bulkActionButtons={false}
                  title=" "
                >
                  <Datagrid>
                    <TextField source="name" label="Wave Name" emptyText="-" />
                    <CustomTimeField
                      source="arrivalTime"
                      label="Bus Arrive by"
                    />
                    <CustomTimeField
                      source="schoolTime"
                      label="School Start Time"
                    />
                    <FunctionField
                      label="Days of the Weeks"
                      render={(record: any) => {
                        let days: string = "";

                        if (record.isMonday) {
                          days = days + " Monday,";
                        }
                        if (record.isTuesday) {
                          days = days + " Tuesday,";
                        }
                        if (record.isWednesday) {
                          days = days + " Wednesday,";
                        }
                        if (record.isThursday) {
                          days = days + " Thursday,";
                        }
                        if (record.isFriday) {
                          days = days + " Friday,";
                        }

                        return days.replace(/,(?=[^,]*$)/, "");
                      }}
                    />
                    <CustomArrayChipField source="levels" chipSource="name" />
                  </Datagrid>
                </List>
              </ResourceContextProvider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Going from School</Typography>
            </Grid>

            <Grid item xs={12}>
              <ResourceContextProvider value="busSchedules">
                <List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<ThisFilter />}
                  filterDefaultValues={{
                    SchoolId: parseInt(props.id),
                    Type: "FromSchool",
                  }}
                  bulkActionButtons={false}
                  title=" "
                >
                  <Datagrid>
                    <TextField source="name" label="Wave Name" emptyText="-" />
                    <CustomTimeField
                      source="arrivalTime"
                      label="Bus Leave by"
                    />
                    <CustomTimeField
                      source="schoolTime"
                      label="School End Time"
                    />
                    <FunctionField
                      label="Days of the Weeks"
                      render={(record: any) => {
                        let days: string = "";

                        if (record.isMonday) {
                          days = days + " Monday,";
                        }
                        if (record.isTuesday) {
                          days = days + " Tuesday,";
                        }
                        if (record.isWednesday) {
                          days = days + " Wednesday,";
                        }
                        if (record.isThursday) {
                          days = days + " Thursday,";
                        }
                        if (record.isFriday) {
                          days = days + " Friday,";
                        }

                        return days.replace(/,(?=[^,]*$)/, "");
                      }}
                    />
                    ;
                    <CustomArrayChipField source="levels" chipSource="name" />
                  </Datagrid>
                </List>
              </ResourceContextProvider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Holidays</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">School Holiday</Typography>
            </Grid>

            <Grid item xs={12}>
              <ResourceContextProvider value="holidays">
                <List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<ThisFilter />}
                  filterDefaultValues={{
                    SchoolId: Number(props.id),
                    AcademicYearId: academicYearId,
                    Type: ETypeHoliday.School,
                  }}
                  bulkActionButtons={false}
                  title=" "
                >
                  <Datagrid>
                    <TextField source="name" label="Holiday" />
                    <CustomDateField source="date" />
                  </Datagrid>
                </List>
              </ResourceContextProvider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Public Holiday</Typography>
            </Grid>

            <Grid item xs={12}>
              <ResourceContextProvider value="holidays">
                <List
                  {...props}
                  exporter={false}
                  hasCreate={false}
                  filters={<ThisFilter />}
                  filterDefaultValues={{
                    SchoolId: Number(props.id),
                    AcademicYearId: academicYearId,
                    Type: ETypeHoliday.Public,
                  }}
                  bulkActionButtons={false}
                  title=" "
                >
                  <Datagrid>
                    <TextField source="name" label="Holiday" />
                    <CustomDateField source="date" />
                  </Datagrid>
                </List>
              </ResourceContextProvider>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} style={GridinRowsWithSpace}>
              <div>
                <h3 className={classes.academicYearLabel}>
                  No Academic Year Active yet.
                </h3>
              </div>
            </Grid>
          </>
        )}
      </SanitizedGrid>
    </>
  );
};

export default Schedule;
